import React, { useMemo } from 'react';
import { entropyStateLabel } from '../entropyLanguage';
import type { VgrHistorySeries } from './diagnoseVgrHistory';
import { vgrHistoryChartSummary } from './diagnoseVgrHistory';
import styles from '../ControlBridge.module.css';

export interface DiagnoseVgrHistoryChartProps {
  series: VgrHistorySeries;
}

function cfBarTone(cf: number | null): 'good' | 'mid' | 'weak' | 'muted' {
  if (cf == null) return 'muted';
  const pct = cf * 100;
  if (pct >= 80) return 'good';
  if (pct >= 55) return 'mid';
  return 'weak';
}

export const DiagnoseVgrHistoryChart: React.FC<DiagnoseVgrHistoryChartProps> = ({ series }) => {
  const summary = useMemo(() => vgrHistoryChartSummary(series), [series]);
  const { points, source } = series;

  if (points.length === 0) {
    return null;
  }

  return (
    <div className={styles.diagVgrHistory} data-testid="diagnose-vgr-history-chart">
      <div className={styles.diagVgrHistoryHeader}>
        <span className={styles.embeddedListLabel}>Operating efficiency trend</span>
        {source === 'proxy' ? (
          <span className={styles.diagVgrHistoryHint}>Estimated from current snapshot</span>
        ) : null}
      </div>
      <div
        className={styles.diagVgrHistoryPlot}
        role="img"
        aria-label={summary}
      >
        <div className={styles.diagVgrHistoryIdeal} aria-hidden="true" title="Ideal 100%" />
        <div className={styles.diagVgrHistoryBars}>
          {points.map((point) => {
            const heightPct =
              point.correctionFactor != null
                ? Math.round(Math.max(0, Math.min(100, point.correctionFactor * 100)))
                : 0;
            const tone = cfBarTone(point.correctionFactor);
            return (
              <div key={point.periodLabel} className={styles.diagVgrHistoryBarCol}>
                <div className={styles.diagVgrHistoryBarTrack}>
                  <div
                    className={`${styles.diagVgrHistoryBarFill} ${styles[`mcProgressFill_${tone}`]}`}
                    style={{ height: `${heightPct}%` }}
                    title={
                      point.correctionFactor != null
                        ? `${point.periodLabel}: ${heightPct}%`
                        : point.periodLabel
                    }
                  />
                </div>
                <span className={styles.diagVgrHistoryBarLabel}>
                  {point.kind === 'today' ? `${point.periodLabel} today` : point.periodLabel}
                  {point.entropy == null
                    ? ''
                    : entropyStateLabel(point.entropy) != null
                      ? ` · ${entropyStateLabel(point.entropy)}`
                      : ` · e${point.entropy}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
